// How HQ's Players screens name a person (spec/14 §10): what the platform record holds, never more.

import type { HqPlayer, HqPlayerSummary } from '@clavian/api-client';
import { isTypeId } from '@clavian/contracts/typeid';

type Named = Pick<HqPlayerSummary, 'id' | 'firstName' | 'lastName' | 'displayName' | 'username'>;

/** The name a person gave — first and last — when they gave one. */
export function fullName(player: Pick<HqPlayerSummary, 'firstName' | 'lastName'>): string | null {
  const name = [player.firstName, player.lastName].filter((part): part is string => part !== null && part.trim() !== '').join(' ');
  return name === '' ? null : name;
}

/** What to call them: their name, else their display name, else their gamer tag, else their id. */
export function playerName(player: Named): string {
  return fullName(player) ?? nonEmpty(player.displayName) ?? nonEmpty(player.username) ?? player.id;
}

function nonEmpty(value: string | null): string | null {
  return value === null || value.trim() === '' ? null : value;
}

/** A player id (`ply_…`), by the contract's own codec. The server checks it again (`400`). */
export const isPlayerId = (value: string): boolean => isTypeId(value, 'ply');

/** The search's bounds (`HqPlayerSearchQuery.q`): the server refuses anything else with `400`. */
export const SEARCH_MIN = 2;
export const SEARCH_MAX = 100;

/** The ban's reason, as `BanReason` bounds it. */
export const BAN_REASON_MAX = 500;

/** Whether a platform ban is on record at all — in force, or lapsed past its expiry. */
export const banOnRecord = (player: Pick<HqPlayer, 'platformBan'>): boolean =>
  player.platformBan.banned || player.platformBan.reason !== null;
